/**
 * STUD PREMIUM SCHEMA
 * Schema dla kreatora reproduktora - PREMIUM_OFFER entity type
 * 
 * ============================================
 * WAGI SCORE (suma = 100) - zgodne z MASTER:
 * ============================================
 * 
 * === MUST completeness: 40 pkt ===
 * - kennel_id / owner info: 5 pkt
 * - name (imię rodowe): 5 pkt
 * - breed: 5 pkt
 * - affiliation_org (ZKwP/FCI): 5 pkt (MUST)
 * - health_tests min 1: 5 pkt (MUST)
 * - stud_price: 8 pkt (MUST dla PREMIUM_OFFER)
 * - stud_fee_includes min 3: 7 pkt (MUST)
 * 
 * === Images: 15 pkt ===
 * - hero (cover_image): 7 pkt (min 1600px)
 * - gallery min 4: 8 pkt (min 1200px)
 * 
 * === Trust: 15 pkt ===
 * - health_tests >= 3: +5 pkt bonus
 * - achievements (championships): +5 pkt
 * - offspring linking (potomstwo w systemie): +5 pkt
 * 
 * === CTA/Flow: 15 pkt ===
 * - contact_method (phone/email/whatsapp): 10 pkt
 * - cta_label ("Zapytaj o krycie"): 5 pkt
 * 
 * === Offer/Price: 15 pkt ===
 * - UWAGA: Liczy się TYLKO gdy:
 *   - stud_price > 0 ORAZ
 *   - stud_fee_includes.length >= 3
 *   W przeciwnym razie = 0 pkt
 * 
 * ============================================
 */

import { FIELD_PRIORITY, CORE_SECTION } from '../core/CoreTypes';

// ==================== SCORE WEIGHTS (MASTER 1:1) ====================

const SCORE_WEIGHTS = {
  // MUST completeness = 40 total
  MUST_KENNEL: 5,
  MUST_NAME: 5,
  MUST_BREED: 5,
  MUST_AFFILIATION: 5,
  MUST_HEALTH_TESTS: 5,
  MUST_STUD_PRICE: 8,
  MUST_FEE_INCLUDES: 7,
  
  // Images = 15 total
  IMAGES_HERO: 7,
  IMAGES_GALLERY: 8,
  
  // Trust = 15 total
  TRUST_HEALTH_TESTS_BONUS: 5,   // >= 3 tests
  TRUST_ACHIEVEMENTS: 5,         // championships/titles
  TRUST_OFFSPRING_LINKING: 5,    // potomstwo w systemie
  
  // CTA/Flow = 15 total
  CTA_CONTACT_METHOD: 10,
  CTA_LABEL: 5,
  
  // Offer/Price = 15 total
  OFFER_COMPLETE: 15            // price + includes
};

// ==================== OPTIONS ====================

/**
 * Organizacje kynologiczne
 */
export const KENNEL_CLUB_OPTIONS = [
  { id: 'zkwp', label: 'ZKWP (Związek Kynologiczny w Polsce)', org: 'ZKWP', affiliation: 'FCI' },
  { id: 'fci', label: 'FCI (bezpośrednio)', org: 'FCI', affiliation: 'FCI' },
  { id: 'akc', label: 'AKC (American Kennel Club)', org: 'AKC', affiliation: 'AKC' },
  { id: 'kc', label: 'KC (The Kennel Club UK)', org: 'KC', affiliation: 'KC' },
  { id: 'vdh', label: 'VDH (Niemcy)', org: 'VDH', affiliation: 'FCI' },
  { id: 'sv', label: 'SV (Verein für Deutsche Schäferhunde)', org: 'SV', affiliation: 'WUSV' },
  { id: 'wusv', label: 'WUSV', org: 'WUSV', affiliation: 'WUSV' },
  { id: 'other', label: 'Inna organizacja', org: '', affiliation: '' },
  { id: 'none', label: 'Poza związkiem (bez rodowodu)', org: '', affiliation: '' },
];

/**
 * Badania zdrowotne - dynamiczne per rasa
 */
export const HEALTH_TEST_OPTIONS = {
  large_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)', required: true },
    { id: 'ed', label: 'ED (dysplazja łokci)', required: true },
    { id: 'spondylosis', label: 'Spondyloza' },
    { id: 'mdr1', label: 'MDR1' },
    { id: 'dm', label: 'DM (mielopatia zwyrodnieniowa)' },
  ],
  medium_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'patella', label: 'Luxacja rzepki' },
  ],
  small_dogs: [
    { id: 'patella', label: 'Luxacja rzepki', required: true },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'heart', label: 'Badanie serca' },
    { id: 'lcp', label: 'Legg-Calvé-Perthes' },
  ],
  default: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'eyes', label: 'Badanie oczu' },
    { id: 'genetic', label: 'Badanie genetyczne' },
  ]
};

/**
 * Co zawiera cena krycia (stud_fee_includes)
 * Minimum 3 wymagane dla pełnego score
 */
export const STUD_FEE_INCLUDES_OPTIONS = [
  { id: 'contract', label: 'Umowa krycia', required: true },
  { id: 'repeat', label: 'Warunki powtórki (jeśli suka nie zajdzie)' },
  { id: 'support', label: 'Wsparcie i konsultacje' },
  { id: 'pedigree_copy', label: 'Kopia rodowodu reproduktora' },
  { id: 'health_results', label: 'Wyniki badań zdrowotnych' },
  { id: 'photos', label: 'Zdjęcia z krycia' },
  { id: 'video', label: 'Film z krycia' },
  { id: 'transport', label: 'Transport/dojazd' },
  { id: 'accommodation', label: 'Zakwaterowanie suczki' },
  { id: 'ai', label: 'Inseminacja (sztuczna)' },
];

/**
 * Championaty i tytuły
 */
export const CHAMPIONSHIP_OPTIONS = [
  { id: 'ch_pl', label: 'Champion Polski (Ch.PL)' },
  { id: 'ch_int', label: 'Interchampion (C.I.B.)' },
  { id: 'ch_club', label: 'Champion Klubowy' },
  { id: 'ch_youth', label: 'Młodzieżowy Champion Polski (Mł.Ch.PL)' },
  { id: 'ch_world', label: 'Champion Świata' },
  { id: 'ch_winner', label: 'Zwycięzca Polski' },
];

/**
 * Osiągnięcia wystawowe
 */
export const SHOW_ACHIEVEMENT_OPTIONS = [
  { id: 'bis', label: 'Best in Show (BIS)' },
  { id: 'bog', label: 'Best of Group (BOG)' },
  { id: 'bob', label: 'Best of Breed (BOB)' },
  { id: 'cacib', label: 'CACIB' },
  { id: 'cac', label: 'CAC' },
  { id: 'res_cacib', label: 'Res. CACIB' },
  { id: 'res_cac', label: 'Res. CAC' },
  { id: 'youth_cac', label: 'Młodzieżowy CAC' },
];

/**
 * Osiągnięcia użytkowe (psy robocze)
 */
export const WORKING_ACHIEVEMENT_OPTIONS = [
  { id: 'va1', label: 'VA1 (Vorzüglich Auslese)' },
  { id: 'va', label: 'VA (Vorzüglich Auslese)' },
  { id: 'v1', label: 'V1 (Vorzüglich)' },
  { id: 'v', label: 'V (Vorzüglich)' },
  { id: 'sg1', label: 'SG1 (Sehr Gut)' },
  { id: 'sg', label: 'SG (Sehr Gut)' },
  { id: 'sieger', label: 'Sieger' },
  { id: 'jugendsieger', label: 'Jugendsieger' },
];

/**
 * Szkolenia użytkowe
 */
export const TRAINING_OPTIONS = {
  working: [
    { id: 'ipo1', label: 'IPO 1' },
    { id: 'ipo2', label: 'IPO 2' },
    { id: 'ipo3', label: 'IPO 3' },
    { id: 'bh', label: 'BH (Begleithund)' },
    { id: 'ztp', label: 'ZTP (Test Charakteru)' },
    { id: 'korung', label: 'Körung' },
    { id: 'ad', label: 'AD (Ausdauerprüfung)' },
  ],
  general: [
    { id: 'pt', label: 'PT (Pies Towarzyszący)' },
    { id: 'obedience', label: 'Obedience' },
    { id: 'agility', label: 'Agility' },
    { id: 'nosework', label: 'Nosework' },
    { id: 'rally_o', label: 'Rally-O' },
    { id: 'canicross', label: 'Canicross' },
  ]
};

// ==================== FIELD DEFINITIONS ====================

export const studPremiumFields = {
  // ========== STEP 1: PODSTAWY ==========
  kennel_id: {
    name: 'kennel_id',
    priority: FIELD_PRIORITY.SHOULD,  // Optional - stud can be independent
    coreSection: 'identity',
    label: 'Hodowla (opcjonalnie)',
    type: 'select',
    step: 1,
    validation: {},
  },
  
  name: {
    name: 'name',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Imię rodowe',
    type: 'text',
    step: 1,
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100
    },
    errors: {
      required: 'Podaj imię reproduktora',
      minLength: 'Imię musi mieć min. 2 znaki'
    }
  },
  
  species: {
    name: 'species',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Gatunek',
    type: 'select',
    step: 1,
    default: 'dog',
    options: [
      { value: 'dog', label: 'Pies' },
      { value: 'cat', label: 'Kot' }
    ],
    validation: { required: true }
  },
  
  breed: {
    name: 'breed',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Rasa',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz rasę'
    }
  },
  
  birth_date: {
    name: 'birth_date',
    priority: FIELD_PRIORITY.SHOULD,  // Recommended but not blocking
    coreSection: 'identity',
    label: 'Data urodzenia',
    type: 'date',
    step: 1,
    validation: {}
  },
  
  color: {
    name: 'color',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Umaszczenie',
    type: 'text',
    step: 1,
    validation: {}
  },
  
  stud_available: {
    name: 'stud_available',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Status dostępności',
    type: 'select',
    step: 1,
    default: true,
    options: [
      { value: true, label: 'Dostępny do krycia' },
      { value: false, label: 'Niedostępny / Wstrzymany' }
    ],
    validation: { required: true }
  },

  // ========== STEP 2: ORGANIZACJA I PRZYNALEŻNOŚĆ (MUST) ==========
  // 
  // UWAGA: Rozdzielone na affiliation_type + affiliation_other_name
  // dla czystszej struktury danych i walidacji
  //
  affiliation_type: {
    name: 'affiliation_type',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'affiliation',
    label: 'Przynależność do związku',
    type: 'select',
    step: 2,
    options: [
      { value: 'zkwp', label: 'ZKWP / FCI' },
      { value: 'fci_direct', label: 'FCI (bezpośrednio)' },
      { value: 'akc', label: 'AKC' },
      { value: 'kc', label: 'KC (UK)' },
      { value: 'vdh', label: 'VDH (Niemcy)' },
      { value: 'sv', label: 'SV / WUSV' },
      { value: 'other', label: 'Inna organizacja' },
      { value: 'none', label: 'Poza związkiem (bez rodowodu)' },
    ],
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz przynależność do związku'
    }
  },
  
  affiliation_other_name: {
    name: 'affiliation_other_name',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'affiliation',
    label: 'Nazwa organizacji',
    type: 'text',
    step: 2,
    placeholder: 'Wpisz nazwę organizacji...',
    // MUST only when affiliation_type === 'other'
    conditionalRequired: {
      field: 'affiliation_type',
      value: 'other'
    },
    validation: {
      minLength: 2
    },
    errors: {
      required: 'Podaj nazwę organizacji',
      minLength: 'Nazwa musi mieć min. 2 znaki'
    }
  },
  
  membership_number: {
    name: 'membership_number',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'affiliation',
    label: 'Numer członkowski / rejestracyjny',
    type: 'text',
    step: 2,
    placeholder: 'np. ZKWP/12345',
    validation: {}
  },
  
  // Legacy field for backward compatibility
  kennel_club: {
    name: 'kennel_club',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'affiliation',
    label: 'Związek kynologiczny (legacy)',
    type: 'text',
    step: 2,
    // Used for data migration / backward compat
    validation: {}
  },
  
  pedigree_number: {
    name: 'pedigree_number',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'affiliation',
    label: 'Numer rodowodu',
    type: 'text',
    step: 2,
    placeholder: 'np. PKR.I-12345',
    validation: {}
  },
  
  pedigree_name: {
    name: 'pedigree_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'affiliation',
    label: 'Pełna nazwa rodowodowa',
    type: 'text',
    step: 2,
    validation: {}
  },

  // ========== STEP 3: ZDROWIE I BADANIA (MUST) ==========
  selected_health_tests: {
    name: 'selected_health_tests',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'trust',
    label: 'Badania zdrowotne',
    type: 'multiselect',
    step: 3,
    validation: {
      required: true,
      minItems: 1
    },
    errors: {
      required: 'Wybierz min. 1 badanie zdrowotne',
      minItems: 'Wybierz min. 1 badanie zdrowotne'
    }
  },
  
  health_test_results: {
    name: 'health_test_results',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Wyniki badań',
    type: 'object',
    step: 3,
    // Dynamic: { hd: 'A', ed: '0', dm: 'N/N' }
    validation: {}
  },
  
  health_tests_text: {
    name: 'health_tests_text',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Dodatkowe informacje o badaniach',
    type: 'textarea',
    step: 3,
    validation: {}
  },

  // ========== STEP 4: OFERTA I CENA (MUST) ==========
  stud_price: {
    name: 'stud_price',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'offer',
    label: 'Cena krycia',
    type: 'text',
    step: 4,
    placeholder: 'np. 2000 zł',
    validation: {
      required: true,
      minLength: 1
    },
    errors: {
      required: 'Podaj cenę krycia'
    }
  },
  
  stud_fee_includes: {
    name: 'stud_fee_includes',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'offer',
    label: 'Co zawiera cena',
    type: 'multiselect',
    step: 4,
    validation: {
      required: true,
      minItems: 3
    },
    errors: {
      required: 'Wybierz co zawiera cena (min. 3)',
      minItems: 'Wybierz min. 3 pozycje'
    }
  },
  
  stud_conditions: {
    name: 'stud_conditions',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Warunki krycia',
    type: 'textarea',
    step: 4,
    placeholder: 'np. wymagane badania suczki, umowa, warunki powtórki...',
    validation: {}
  },

  // ========== STEP 5: OSIĄGNIĘCIA + POTOMSTWO ==========
  selected_championships: {
    name: 'selected_championships',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Championaty',
    type: 'multiselect',
    step: 5,
    validation: {}
  },
  
  selected_show_achievements: {
    name: 'selected_show_achievements',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Osiągnięcia wystawowe',
    type: 'multiselect',
    step: 5,
    validation: {}
  },
  
  selected_working_achievements: {
    name: 'selected_working_achievements',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Osiągnięcia użytkowe',
    type: 'multiselect',
    step: 5,
    validation: {}
  },
  
  selected_training: {
    name: 'selected_training',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Szkolenia i egzaminy',
    type: 'multiselect',
    step: 5,
    validation: {}
  },
  
  training_text: {
    name: 'training_text',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Inne szkolenia (tekst)',
    type: 'textarea',
    step: 5,
    validation: {}
  },
  
  // Potomstwo - linking
  offspring_refs: {
    name: 'offspring_refs',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Potomstwo (mioty w systemie)',
    type: 'multiselect',
    step: 5,
    // Array of litter IDs linked to this stud
    validation: {}
  },
  
  offspring_note: {
    name: 'offspring_note',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Potomstwo (tekst - poza systemem)',
    type: 'textarea',
    step: 5,
    placeholder: 'Opisz potomstwo jeśli nie ma go w systemie...',
    validation: {}
  },

  // ========== STEP 6: ZDJĘCIA + KONTAKT (MUST) ==========
  main_photo: {
    name: 'main_photo',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'images',
    label: 'Zdjęcie główne',
    type: 'image',
    step: 6,
    validation: {
      required: true
    },
    errors: {
      required: 'Dodaj zdjęcie główne'
    }
  },
  
  gallery: {
    name: 'gallery',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'images',
    label: 'Galeria zdjęć',
    type: 'gallery',
    step: 6,
    validation: {
      required: true,
      minItems: 2
    },
    errors: {
      required: 'Dodaj min. 2 zdjęcia do galerii',
      minItems: 'Dodaj min. 2 zdjęcia do galerii'
    }
  },
  
  // Contact - at least one method is MUST
  contact_phone: {
    name: 'contact_phone',
    priority: FIELD_PRIORITY.SHOULD,  // One of phone/email is MUST (custom validation)
    coreSection: 'cta',
    label: 'Telefon',
    type: 'tel',
    step: 6,
    validation: {}
  },
  
  contact_whatsapp: {
    name: 'contact_whatsapp',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'cta',
    label: 'WhatsApp',
    type: 'tel',
    step: 6,
    validation: {}
  },
  
  contact_email: {
    name: 'contact_email',
    priority: FIELD_PRIORITY.SHOULD,  // One of phone/email is MUST (custom validation)
    coreSection: 'cta',
    label: 'Email',
    type: 'email',
    step: 6,
    validation: {}
  },
  
  contact_facebook: {
    name: 'contact_facebook',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'cta',
    label: 'Facebook',
    type: 'url',
    step: 6,
    validation: {}
  },
  
  contact_instagram: {
    name: 'contact_instagram',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'cta',
    label: 'Instagram',
    type: 'url',
    step: 6,
    validation: {}
  },
  
  // CTA Label - editable
  cta_label: {
    name: 'cta_label',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'cta',
    label: 'Tekst przycisku kontaktu',
    type: 'text',
    step: 6,
    default: 'Zapytaj o krycie',
    placeholder: 'np. Zapytaj o krycie',
    validation: {}
  },

  // Location (for independent studs)
  city: {
    name: 'city',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Miasto',
    type: 'text',
    step: 6,
    validation: {
      required: true
    },
    errors: {
      required: 'Podaj miasto'
    }
  },
  
  voivodeship: {
    name: 'voivodeship',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Województwo',
    type: 'select',
    step: 6,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz województwo'
    }
  },

  // Description
  description: {
    name: 'description',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'identity',
    label: 'Opis reproduktora',
    type: 'textarea',
    step: 6,
    validation: {}
  },

  // Layout preference
  page_layout: {
    name: 'page_layout',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'identity',
    label: 'Styl strony',
    type: 'select',
    step: 6,
    default: 'modern',
    options: [
      { value: 'modern', label: 'Nowoczesny' },
      { value: 'classic', label: 'Klasyczny' }
    ],
    validation: {}
  },

  // Pedigree files
  pedigree_file: {
    name: 'pedigree_file',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Plik rodowodu (PDF/JPG)',
    type: 'file',
    step: 2,
    validation: {}
  },
  
  pedigree_link: {
    name: 'pedigree_link',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Link do rodowodu online',
    type: 'url',
    step: 2,
    validation: {}
  },

  // Parents info
  sire_name: {
    name: 'sire_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Imię ojca',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  sire_titles: {
    name: 'sire_titles',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Tytuły ojca',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  dam_name: {
    name: 'dam_name',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Imię matki',
    type: 'text',
    step: 2,
    validation: {}
  },
  
  dam_titles: {
    name: 'dam_titles',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'trust',
    label: 'Tytuły matki',
    type: 'text',
    step: 2,
    validation: {}
  },
};

// ==================== STEP CONFIGURATION ====================
// 
// 6 kroków zgodnych ze specyfikacją:
// 1. Podstawy (identity)
// 2. Organizacja i przynależność (affiliation) - MUST
// 3. Zdrowie i badania (trust) - MUST
// 4. Oferta i cena (offer) - MUST
// 5. Osiągnięcia + Potomstwo (trust bonus)
// 6. Zdjęcia + Kontakt (images + cta) - MUST
//

export const studSteps = [
  {
    id: 1,
    name: 'Pies',
    subtitle: 'Imię i rasa',
    icon: 'dog',
    fields: ['kennel_id', 'name', 'species', 'breed', 'birth_date', 'color', 'stud_available'],
    mustFields: ['name', 'breed'],
    mustGate: true
  },
  {
    id: 2,
    name: 'Rodowód',
    subtitle: 'Przynależność i rodzice',
    icon: 'badge',
    fields: ['affiliation_type', 'affiliation_other_name', 'membership_number', 'pedigree_number', 'pedigree_name', 'pedigree_file', 'pedigree_link', 'sire_name', 'sire_titles', 'dam_name', 'dam_titles'],
    mustFields: [],
    customValidation: 'affiliation',
    mustGate: false
  },
  {
    id: 3,
    name: 'Zdrowie',
    subtitle: 'Badania i wyniki',
    icon: 'heart',
    fields: ['selected_health_tests', 'health_test_results', 'health_tests_text'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 4,
    name: 'Oferta',
    subtitle: 'Cena i warunki krycia',
    icon: 'dollar',
    fields: ['stud_price', 'stud_fee_includes', 'stud_conditions'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 5,
    name: 'Osiągnięcia',
    subtitle: 'Tytuły i wyróżnienia',
    icon: 'crown',
    fields: ['selected_championships', 'selected_show_achievements', 'selected_working_achievements', 'selected_training', 'training_text', 'offspring_refs', 'offspring_note'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 6,
    name: 'Zdjęcia i kontakt',
    subtitle: 'Galeria i dane kontaktowe',
    icon: 'camera',
    fields: ['main_photo', 'gallery', 'description', 'city', 'voivodeship', 'contact_phone', 'contact_whatsapp', 'contact_email', 'contact_facebook', 'contact_instagram', 'cta_label', 'page_layout'],
    mustFields: ['main_photo', 'city'],
    customValidation: 'contact',
    mustGate: true
  },
  {
    id: 7,
    name: 'Podsumowanie',
    subtitle: 'Sprawdź i opublikuj',
    icon: 'check',
    fields: [],
    mustFields: [],
    mustGate: false
  }
];

// ==================== SCHEMA EXPORT ====================

export const studPremiumSchema = {
  entityType: 'PREMIUM_OFFER',
  profileId: 'stud_default',
  steps: studSteps,
  fields: studPremiumFields
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Check if gallery has minimum required images
 * Zmieniono z 4 na 2 - prostszy UX
 */
export function hasSufficientGallery(formData) {
  const gallery = formData.gallery || [];
  return gallery.length >= 2;
}

/**
 * Check if stud fee includes minimum items
 * Zmieniono z 3 na 2 - prostszy UX
 */
export function hasSufficientFeeIncludes(formData) {
  const includes = formData.stud_fee_includes || [];
  return includes.length >= 2;
}

/**
 * Check if health tests are filled (min 1)
 */
export function hasHealthTests(formData) {
  const tests = formData.selected_health_tests || [];
  return tests.length >= 1;
}

/**
 * Check if affiliation is complete
 * affiliation_type is MUST
 * affiliation_other_name is MUST only when affiliation_type === 'other'
 */
export function hasAffiliation(formData) {
  if (!formData.affiliation_type) {
    // Backward compat: check legacy kennel_club field
    if (formData.kennel_club && formData.kennel_club.trim().length > 0) {
      return true;
    }
    return false;
  }
  if (formData.affiliation_type === 'other') {
    return formData.affiliation_other_name && formData.affiliation_other_name.trim().length >= 2;
  }
  return true;
}

/**
 * Check if contact method is provided (phone or email)
 */
export function hasContactMethod(formData) {
  return (
    (formData.contact_phone && formData.contact_phone.trim().length > 0) ||
    (formData.contact_email && formData.contact_email.trim().length > 0) ||
    (formData.contact_whatsapp && formData.contact_whatsapp.trim().length > 0)
  );
}

/**
 * Check if offer is complete (price + includes >= 3)
 */
export function hasCompleteOffer(formData) {
  const hasPrice = formData.stud_price && formData.stud_price.toString().trim().length > 0;
  const hasIncludes = hasSufficientFeeIncludes(formData);
  return hasPrice && hasIncludes;
}

/**
 * Check if has achievements (championships or show/working achievements)
 */
export function hasAchievements(formData) {
  return (
    (formData.selected_championships || []).length > 0 ||
    (formData.selected_show_achievements || []).length > 0 ||
    (formData.selected_working_achievements || []).length > 0
  );
}

/**
 * Check if has offspring linking (ID refs, not just text)
 */
export function hasOffspringLinking(formData) {
  return (formData.offspring_refs || []).length > 0;
}

// ==================== QUALITY SCORE CALCULATION ====================

/**
 * Calculate Quality Score for Stud (PREMIUM_OFFER)
 * Returns: { score: 0-100, breakdown: {...}, suggestions: [...] }
 */
export function calculateStudQualityScore(formData) {
  let score = 0;
  const breakdown = {
    must: 0,
    images: 0,
    trust: 0,
    cta: 0,
    offer: 0
  };
  const suggestions = [];

  // ========== MUST COMPLETENESS (40 pkt) ==========
  
  // Name (5 pkt)
  if (formData.name && formData.name.trim().length >= 2) {
    breakdown.must += SCORE_WEIGHTS.MUST_NAME;
  } else {
    suggestions.push({ field: 'name', message: 'Dodaj imię reproduktora', step: 1 });
  }
  
  // Breed (5 pkt)
  if (formData.breed && formData.breed.trim().length > 0) {
    breakdown.must += SCORE_WEIGHTS.MUST_BREED;
  } else {
    suggestions.push({ field: 'breed', message: 'Wybierz rasę', step: 1 });
  }
  
  // Affiliation (5 pkt)
  if (hasAffiliation(formData)) {
    breakdown.must += SCORE_WEIGHTS.MUST_AFFILIATION;
  } else {
    suggestions.push({ field: 'affiliation_type', message: 'Wybierz przynależność do związku', step: 2 });
  }
  
  // Health tests (5 pkt)
  if (hasHealthTests(formData)) {
    breakdown.must += SCORE_WEIGHTS.MUST_HEALTH_TESTS;
  } else {
    suggestions.push({ field: 'selected_health_tests', message: 'Dodaj min. 1 badanie zdrowotne', step: 3 });
  }
  
  // Stud price (8 pkt)
  if (formData.stud_price && formData.stud_price.toString().trim().length > 0) {
    breakdown.must += SCORE_WEIGHTS.MUST_STUD_PRICE;
  } else {
    suggestions.push({ field: 'stud_price', message: 'Podaj cenę krycia', step: 4 });
  }
  
  // Fee includes (7 pkt)
  if (hasSufficientFeeIncludes(formData)) {
    breakdown.must += SCORE_WEIGHTS.MUST_FEE_INCLUDES;
  } else {
    const count = (formData.stud_fee_includes || []).length;
    suggestions.push({ field: 'stud_fee_includes', message: `Wybierz co zawiera cena (masz ${count}/3 min.)`, step: 4 });
  }

  // ========== IMAGES (15 pkt) ==========
  
  // Hero image (7 pkt)
  if (formData.main_photo) {
    breakdown.images += SCORE_WEIGHTS.IMAGES_HERO;
  } else {
    suggestions.push({ field: 'main_photo', message: 'Dodaj zdjęcie główne', step: 6 });
  }
  
  // Gallery (8 pkt)
  if (hasSufficientGallery(formData)) {
    breakdown.images += SCORE_WEIGHTS.IMAGES_GALLERY;
  } else {
    const count = (formData.gallery || []).length;
    suggestions.push({ field: 'gallery', message: `Dodaj zdjęcia do galerii (masz ${count}/4 min.)`, step: 6 });
  }

  // ========== TRUST (15 pkt) ==========
  
  // Health tests bonus (5 pkt) - >= 3 tests
  if ((formData.selected_health_tests || []).length >= 3) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_HEALTH_TESTS_BONUS;
  }
  
  // Achievements (5 pkt)
  if (hasAchievements(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_ACHIEVEMENTS;
  }
  
  // Offspring linking (5 pkt)
  if (hasOffspringLinking(formData)) {
    breakdown.trust += SCORE_WEIGHTS.TRUST_OFFSPRING_LINKING;
  }

  // ========== CTA/FLOW (15 pkt) ==========
  
  // Contact method (10 pkt)
  if (hasContactMethod(formData)) {
    breakdown.cta += SCORE_WEIGHTS.CTA_CONTACT_METHOD;
  } else {
    suggestions.push({ field: 'contact_phone', message: 'Dodaj telefon lub email kontaktowy', step: 6 });
  }
  
  // CTA label - assumed if offer is complete (5 pkt)
  if (hasCompleteOffer(formData)) {
    breakdown.cta += SCORE_WEIGHTS.CTA_LABEL;
  }

  // ========== OFFER/PRICE (15 pkt) ==========
  // All-or-nothing: price + includes >= 3
  if (hasCompleteOffer(formData)) {
    breakdown.offer += SCORE_WEIGHTS.OFFER_COMPLETE;
  }

  // Calculate total
  score = breakdown.must + breakdown.images + breakdown.trust + breakdown.cta + breakdown.offer;
  
  // Cap at 100
  score = Math.min(100, Math.max(0, score));

  return {
    score,
    breakdown,
    suggestions,
    status: getScoreStatus(score)
  };
}

/**
 * Get status label based on score
 */
function getScoreStatus(score) {
  if (score >= 90) return { label: 'Doskonały', color: 'emerald', icon: 'star' };
  if (score >= 70) return { label: 'Bardzo dobry', color: 'green', icon: 'check' };
  if (score >= 50) return { label: 'Dobry', color: 'yellow', icon: 'trending-up' };
  if (score >= 30) return { label: 'Wymaga uzupełnienia', color: 'orange', icon: 'alert' };
  return { label: 'Rozpoczęty', color: 'red', icon: 'edit' };
}

/**
 * Get missing MUST items with user-friendly messages
 */
export function getStudMissingItems(formData) {
  const missing = [];
  
  if (!formData.name || formData.name.trim().length < 2) {
    missing.push({ 
      field: 'name', 
      label: 'Imię reproduktora',
      step: 1,
      message: 'Podaj imię reproduktora'
    });
  }
  
  if (!formData.breed) {
    missing.push({ 
      field: 'breed', 
      label: 'Rasa',
      step: 1,
      message: 'Wybierz rasę'
    });
  }
  
  if (!hasAffiliation(formData)) {
    missing.push({ 
      field: 'affiliation_type', 
      label: 'Przynależność do związku',
      step: 2,
      message: 'Wybierz przynależność do związku kynologicznego'
    });
  }
  
  if (!hasHealthTests(formData)) {
    missing.push({ 
      field: 'selected_health_tests', 
      label: 'Badania zdrowotne',
      step: 3,
      message: 'Dodaj min. 1 badanie zdrowotne'
    });
  }
  
  if (!formData.stud_price || formData.stud_price.toString().trim().length === 0) {
    missing.push({ 
      field: 'stud_price', 
      label: 'Cena krycia',
      step: 4,
      message: 'Podaj cenę krycia'
    });
  }
  
  if (!hasSufficientFeeIncludes(formData)) {
    const count = (formData.stud_fee_includes || []).length;
    missing.push({ 
      field: 'stud_fee_includes', 
      label: 'Co zawiera cena',
      step: 4,
      message: `Wybierz co zawiera cena (masz ${count}/3 min.)`
    });
  }
  
  if (!formData.main_photo) {
    missing.push({ 
      field: 'main_photo', 
      label: 'Zdjęcie główne',
      step: 6,
      message: 'Dodaj zdjęcie główne reproduktora'
    });
  }
  
  if (!hasSufficientGallery(formData)) {
    const count = (formData.gallery || []).length;
    missing.push({ 
      field: 'gallery', 
      label: 'Galeria zdjęć',
      step: 6,
      message: `Dodaj zdjęcia do galerii (masz ${count}/4 min.)`
    });
  }
  
  if (!formData.city) {
    missing.push({ 
      field: 'city', 
      label: 'Miasto',
      step: 6,
      message: 'Podaj miasto'
    });
  }
  
  if (!formData.voivodeship) {
    missing.push({ 
      field: 'voivodeship', 
      label: 'Województwo',
      step: 6,
      message: 'Wybierz województwo'
    });
  }
  
  // Contact method is MUST (at least phone or email)
  if (!hasContactMethod(formData)) {
    missing.push({ 
      field: 'contact_phone', 
      label: 'Dane kontaktowe',
      step: 6,
      message: 'Podaj telefon lub email kontaktowy'
    });
  }
  
  return missing;
}

// ==================== IMAGE REQUIREMENTS ====================

export const IMAGE_REQUIREMENTS = {
  hero: {
    minWidth: 1600,
    minHeight: 900,
    aspectRatio: '16:9',
    description: 'Hero image min. 1600×900px, format 16:9'
  },
  gallery: {
    minWidth: 1200,
    minHeight: 800,
    minCount: 2,
    maxCount: 20,
    description: 'Min. 2 zdjęcia, zalecana szerokość 1200px+'
  }
};

export default studPremiumSchema;
